import { DI } from 'aurelia';

/**
 * All sounds are synthesised with the Web Audio API so the app ships no audio
 * assets. Each sound is a short envelope on one or two oscillators.
 */
export interface ISound {
  muted: boolean;
  toggle(): void;
  unlock(): void;
  tick(): void;
  ballPop(index: number): void;
  hit(streak: number): void;
  bet(): void;
  win(big: boolean): void;
  lose(): void;
  coin(): void;
  error(): void;
  close(): void;
  reelTick(): void;
  reelStop(good: boolean): void;
  coinFlip(): void;
  countdown(final: boolean): void;
}

export const ISound = DI.createInterface<ISound>('ISound', (x) => x.singleton(SoundService));

const STORAGE_KEY = 'bingodash.muted';

export class SoundService implements ISound {
  muted = false;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  constructor() {
    try { this.muted = localStorage.getItem(STORAGE_KEY) === '1'; } catch { /* ignore */ }
  }

  toggle(): void {
    this.muted = !this.muted;
    try { localStorage.setItem(STORAGE_KEY, this.muted ? '1' : '0'); } catch { /* ignore */ }
    if (!this.muted) { this.unlock(); this.bet(); }
  }

  /** Browsers need a user gesture before audio can start. Call from any click. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.35;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private tone(freq: number, opts: { type?: OscillatorType; dur?: number; gain?: number; at?: number; slideTo?: number; attack?: number } = {}): void {
    if (this.muted || !this.ctx || !this.master) return;
    const { type = 'sine', dur = 0.12, gain = 1, at = 0, slideTo, attack = 0.005 } = opts;
    const t0 = this.ctx.currentTime + at;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private noise(dur: number, gain: number, at = 0): void {
    if (this.muted || !this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + at;
    const buf = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * dur), this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.value = 1800;
    src.connect(filt).connect(g).connect(this.master);
    src.start(t0);
  }

  tick(): void { this.tone(1400, { type: 'square', dur: 0.04, gain: 0.25 }); }

  /** Pitch climbs a little across the 20 balls so the reveal builds. */
  ballPop(index: number): void {
    const base = 320 + index * 14;
    this.noise(0.05, 0.5);
    this.tone(base, { type: 'triangle', dur: 0.14, gain: 0.7, slideTo: base * 1.6 });
  }

  hit(streak: number): void {
    const root = 660 * Math.pow(1.0595, Math.min(streak, 10) * 2);
    this.tone(root, { type: 'sine', dur: 0.18, gain: 0.9 });
    this.tone(root * 1.5, { type: 'sine', dur: 0.26, gain: 0.5, at: 0.05 });
  }

  bet(): void {
    this.tone(880, { type: 'square', dur: 0.05, gain: 0.3 });
    this.tone(1320, { type: 'square', dur: 0.07, gain: 0.25, at: 0.05 });
  }

  win(big: boolean): void {
    const notes = big ? [523, 659, 784, 1047, 1319, 1568] : [523, 659, 784, 1047];
    notes.forEach((n, i) => this.tone(n, { type: 'triangle', dur: 0.22, gain: 0.8, at: i * 0.09 }));
    if (big) {
      for (let i = 0; i < 6; i++) this.tone(1568 + i * 60, { type: 'sine', dur: 0.4, gain: 0.35, at: 0.6 + i * 0.08 });
    }
  }

  lose(): void { this.tone(220, { type: 'sine', dur: 0.3, gain: 0.5, slideTo: 140 }); }

  coin(): void {
    this.tone(1760, { type: 'sine', dur: 0.08, gain: 0.5 });
    this.tone(2637, { type: 'sine', dur: 0.3, gain: 0.5, at: 0.08 });
  }

  error(): void { this.tone(160, { type: 'square', dur: 0.15, gain: 0.3 }); }

  close(): void {
    this.tone(700, { type: 'triangle', dur: 0.1, gain: 0.5 });
    this.tone(500, { type: 'triangle', dur: 0.2, gain: 0.5, at: 0.1 });
  }

  reelTick(): void { this.tone(2200, { type: 'square', dur: 0.018, gain: 0.12 }); }

  reelStop(good: boolean): void {
    this.noise(0.04, 0.4);
    if (good) {
      [784, 988, 1175, 1568].forEach((n, i) => this.tone(n, { type: 'triangle', dur: 0.16, gain: 0.55, at: 0.04 + i * 0.07 }));
    } else {
      this.tone(392, { type: 'triangle', dur: 0.14, gain: 0.4, at: 0.03 });
    }
  }

  /** metallic ping plus a spin-down wobble */
  coinFlip(): void {
    for (let i = 0; i < 7; i++) this.tone(2400 + (i % 2) * 300, { type: 'sine', dur: 0.05, gain: 0.18 - i * 0.02, at: i * (0.05 + i * 0.012) });
    this.tone(3136, { type: 'sine', dur: 0.5, gain: 0.25, at: 0.62 });
  }

  countdown(final: boolean): void {
    this.tone(final ? 1320 : 990, { type: 'square', dur: final ? 0.25 : 0.06, gain: 0.25 });
  }
}
